import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Category } from '../../../shared/types';
import { useShop } from '../../../shared/store';
import { creatorCatalogService } from '../../../shared/services/creatorCatalogService';
import { consumeBuyerRecordedAudioDraft } from '../utils/buyerRecordedAudioDraft';
import { consumeBuyerRecordedVideoDraft } from '../utils/buyerRecordedVideoDraft';

type BuyerDraftKind = 'book' | 'audio' | 'animation' | 'pod';
type FeedbackStatus = 'idle' | 'pending' | 'success' | 'error';

interface BuyerCreateContentPanelProps {
  categories: Category[];
}

interface FormFeedback {
  status: FeedbackStatus;
  message: string;
}

interface PublisherCongratsState {
  isOpen: boolean;
  publisherName: string;
}

const initialFeedback: FormFeedback = {
  status: 'idle',
  message: '',
};

const getFeedbackTone = (status: FeedbackStatus): string => {
  if (status === 'success') {
    return 'text-emerald-700';
  }
  if (status === 'error') {
    return 'text-red-600';
  }
  if (status === 'pending') {
    return 'text-orange-600';
  }

  return 'text-slate-500';
};

export const BuyerCreateContentPanel = ({ categories }: BuyerCreateContentPanelProps) => {
  const navigate = useNavigate();
  const { currentUser, products, refreshCatalog } = useShop();
  const [importedRecordedDraft] = useState(() => consumeBuyerRecordedAudioDraft());
  const [importedRecordedVideoDraft] = useState(() => consumeBuyerRecordedVideoDraft());
  const [contentKind, setContentKind] = useState<BuyerDraftKind>(
    importedRecordedDraft ? 'audio' : importedRecordedVideoDraft ? 'animation' : 'book',
  );
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [categoryKey, setCategoryKey] = useState('');
  const [language, setLanguage] = useState('en');
  const [coverFile, setCoverFile] = useState<File | null>(importedRecordedDraft?.imageFile ?? null);
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(importedRecordedDraft?.file ?? null);
  const [trailerFile, setTrailerFile] = useState<File | null>(importedRecordedVideoDraft?.videoFile ?? null);
  const [fullVideoFile, setFullVideoFile] = useState<File | null>(importedRecordedVideoDraft?.videoFile ?? null);
  const [pictureFile, setPictureFile] = useState<File | null>(importedRecordedVideoDraft?.posterFile ?? null);
  const [podPageCount, setPodPageCount] = useState('');
  const [podSize, setPodSize] = useState('A5');
  const [podColorType, setPodColorType] = useState('bw');
  const [podCoverType, setPodCoverType] = useState('soft');
  const [, setPodInteriorPdf] = useState<File | null>(null);
  const [, setPodCoverPdf] = useState<File | null>(null);
  const [podInteriorPdfUrl, setPodInteriorPdfUrl] = useState('');
  const [podCoverPdfUrl, setPodCoverPdfUrl] = useState('');
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<FormFeedback>(
    importedRecordedDraft
      ? {
          status: 'success',
          message: `Recorder draft "${importedRecordedDraft.file.name}" attached${importedRecordedDraft.imageFile ? ' with its cover image' : ''}. Add the remaining details, then save your content draft.`,
        }
      : importedRecordedVideoDraft
        ? {
            status: 'success',
            message: `Video studio draft "${importedRecordedVideoDraft.videoFile.name}" attached. Trailer, full video, and poster image were filled automatically. You can replace any of them before saving.`,
          }
      : initialFeedback,
  );
  const [publisherCongrats, setPublisherCongrats] = useState<PublisherCongratsState>({
    isOpen: false,
    publisherName: '',
  });

  const resolvePublisherName = (): string => {
    const explicitName = currentUser?.name?.trim();
    if (explicitName) {
      return explicitName;
    }

    const email = currentUser?.email?.trim().toLowerCase();
    if (!email) {
      return 'Haapa Publisher';
    }

    const [localPart] = email.split('@');
    return localPart || 'Haapa Publisher';
  };

  const userAlreadyHasPublishedContent = (): boolean => {
    const currentUserId = currentUser?.id?.trim();
    const currentEmail = currentUser?.email?.trim().toLowerCase();
    const publisherName = resolvePublisherName().toLowerCase();

    return products.some((product) => {
      const uploaderId = product.uploaderId?.trim();
      const uploaderEmail = product.uploaderEmail?.trim().toLowerCase();
      const uploadedBy = product.uploaderName?.trim().toLowerCase();
      const publishedCompany = product.publishedCompany?.trim().toLowerCase();
      const authorName = product.authorName?.trim().toLowerCase() || product.author?.trim().toLowerCase();

      return Boolean(
        (currentUserId && uploaderId && uploaderId === currentUserId) ||
          (currentEmail && uploaderEmail && uploaderEmail === currentEmail) ||
          (uploadedBy && uploadedBy === publisherName) ||
          (publishedCompany && publishedCompany === publisherName) ||
          (authorName && authorName === publisherName),
      );
    });
  };

  const resetTypeSpecificFields = (nextKind: BuyerDraftKind) => {
    setContentKind(nextKind);
    setFeedback(initialFeedback);
    setFormErrors([]);
    setCoverFile(null);
    setBookFile(null);
    setAudioFile(null);
    setTrailerFile(null);
    setFullVideoFile(null);
    setPictureFile(null);
    setPodPageCount('');
    setPodSize('A5');
    setPodColorType('bw');
    setPodCoverType('soft');
    setPodInteriorPdf(null);
    setPodCoverPdf(null);
    setPodInteriorPdfUrl('');
    setPodCoverPdfUrl('');
  };

  const clearFieldError = (keyword: string) => {
    setFormErrors((previous) => previous.filter((error) => !error.toLowerCase().includes(keyword.toLowerCase())));
    if (feedback.status === 'error') {
      setFeedback(initialFeedback);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: string[] = [];

    if (!title.trim()) {
      nextErrors.push('Title is required.');
    }

    if (contentKind !== 'pod' && !summary.trim()) {
      nextErrors.push('Summary is required.');
    }

    if (contentKind === 'book' && !bookFile) {
      nextErrors.push('Attach a book file for your draft.');
    }

    if (contentKind === 'audio' && !audioFile) {
      nextErrors.push('Attach an audio file for your draft.');
    }

    if (contentKind === 'animation' && (!trailerFile || !fullVideoFile || !pictureFile)) {
      nextErrors.push('Trailer, full video, and picture are required for animation drafts.');
    }

    if (contentKind === 'pod') {
      const parsedPageCount = Number(podPageCount);
      if (!Number.isFinite(parsedPageCount) || parsedPageCount <= 0) {
        nextErrors.push('pageCount is required and must be greater than 0.');
      }
    }

    if (!language.trim()) {
      nextErrors.push('Language is required.');
    }

    if (nextErrors.length > 0) {
      setFormErrors(nextErrors);
      setFeedback({ status: 'error', message: 'Please fix the errors highlighted below.' });
      return;
    }

    setFormErrors([]);

    if (contentKind === 'pod') {
      setFeedback({ status: 'pending', message: 'Saving your POD draft...' });
      await new Promise((resolve) => setTimeout(resolve, 350));
      setTitle('');
      setSummary('');
      setCategoryKey('');
      setLanguage('en');
      resetTypeSpecificFields('book');
      setFeedback({
        status: 'success',
        message: 'POD draft saved successfully.',
      });
      return;
    }

    if (!currentUser?.id) {
      setFeedback({ status: 'error', message: 'You must be signed in to publish content.' });
      return;
    }

    const firstPublisherUpload = !userAlreadyHasPublishedContent();
    const publisherName = resolvePublisherName();

    try {
      setFeedback({ status: 'pending', message: 'Publishing your content...' });

      await creatorCatalogService.publish({
        kind: contentKind,
        title,
        summary,
        categoryKey: categoryKey || 'uncategorized',
        language,
        coverFile,
        bookFile,
        audioFile,
        trailerFile,
        fullVideoFile,
        pictureFile,
        publisherName,
        publisherEmail: currentUser.email,
        publisherPhone: currentUser.phone,
        publisherUserId: currentUser.id,
      });

      await refreshCatalog();

      setTitle('');
      setSummary('');
      setCategoryKey('');
      setLanguage('en');
      resetTypeSpecificFields('book');
      setFeedback({
        status: 'success',
        message: firstPublisherUpload
          ? 'Content published successfully. You are now listed as a publisher.'
          : 'Content published successfully.',
      });

      if (firstPublisherUpload) {
        setPublisherCongrats({
          isOpen: true,
          publisherName,
        });
      }
    } catch (error: unknown) {
      setFeedback({
        status: 'error',
        message: error instanceof Error ? error.message : 'Unable to publish content.',
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="grid gap-4 text-sm">
        {formErrors.length > 0 && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-red-700">
            <p className="font-semibold">Please address the following:</p>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">
              {formErrors.map((error, index) => (
                <li key={`buyer-draft-error-${index}`}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      <section className="space-y-3 rounded-xl border border-orange-100 p-3">
        <div className="grid gap-1 sm:max-w-xs">
          <span className="text-slate-600">Content Type</span>
          <select
            value={contentKind}
            onChange={(event) => resetTypeSpecificFields(event.target.value as BuyerDraftKind)}
            className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
          >
            <option value="book">Book</option>
            <option value="audio">Audio</option>
            <option value="animation">Animation</option>
            <option value="pod">POD Book</option>
          </select>
        </div>
        <p className="text-xs text-slate-500">
          {contentKind === 'pod'
            ? 'Create a POD draft with name and pageCount required. All other POD fields are optional.'
            : contentKind === 'book'
              ? 'Prepare a book draft with the fields needed for book publishing.'
              : contentKind === 'audio'
                ? 'Prepare an audio draft with the fields needed for audio publishing.'
                : 'Prepare an animation draft with the fields needed for animation publishing.'}
        </p>
      </section>

      <section className="space-y-3 rounded-xl border border-orange-100 p-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">
            {contentKind === 'pod' ? '1. POD Details' : '1. Content Info'}
          </h4>
          <span className="text-xs text-slate-500">
            {contentKind === 'pod' ? 'name and pageCount required' : 'Required'}
          </span>
        </div>

        <label className="grid gap-1">
          <span className="text-slate-600">{contentKind === 'pod' ? 'name *' : 'Content Name *'}</span>
          <input
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              clearFieldError('title');
            }}
            placeholder={contentKind === 'pod' ? 'My POD title' : 'Content name...'}
            className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
          />
        </label>

        {contentKind === 'pod' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1">
              <span className="text-slate-600">pageCount *</span>
              <input
                type="number"
                min="1"
                required
                value={podPageCount}
                onChange={(event) => {
                  setPodPageCount(event.target.value);
                  clearFieldError('pagecount');
                }}
                placeholder="120"
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-slate-600">size</span>
              <select
                value={podSize}
                onChange={(event) => setPodSize(event.target.value)}
                className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
              >
                <option value="A5">A5</option>
                <option value="A4">A4</option>
                <option value="B5">B5</option>
              </select>
            </label>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
              <span className="text-slate-600">Content Category</span>
              <select
                value={categoryKey}
                onChange={(event) => {
                  setCategoryKey(event.target.value);
                  clearFieldError('category');
                }}
                className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
              >
                <option value="">Content category...</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.slug || category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">Language</span>
                <select
                  value={language}
                  onChange={(event) => {
                    setLanguage(event.target.value);
                    clearFieldError('language');
                  }}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="">Choose language</option>
                  <option value="en">English</option>
                  <option value="rw">Kinyarwanda</option>
                  <option value="sw">Swahili</option>
                  <option value="fr">French</option>
                </select>
              </label>
            </div>

            <label className="grid gap-1">
              <span className="text-slate-600">Content Summary *</span>
              <textarea
                rows={3}
                value={summary}
                onChange={(event) => {
                  setSummary(event.target.value);
                  clearFieldError('summary');
                }}
                placeholder="Content summary..."
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
          </>
        )}
      </section>

      <section className="space-y-3 rounded-xl border border-orange-100 p-3">
        <h4 className="text-sm font-semibold text-slate-900">
          {contentKind === 'pod' ? '2. POD Files And URLs' : '2. Content Medias'}
        </h4>

        {contentKind === 'pod' ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">colorType</span>
                <select
                  value={podColorType}
                  onChange={(event) => setPodColorType(event.target.value)}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="bw">Black & White</option>
                  <option value="color">Full Color</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">coverType</span>
                <select
                  value={podCoverType}
                  onChange={(event) => setPodCoverType(event.target.value)}
                  className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
                >
                  <option value="soft">Soft Cover</option>
                  <option value="hard">Hard Cover</option>
                </select>
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">interiorPdf</span>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(event) => setPodInteriorPdf(event.target.files?.[0] ?? null)}
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">coverPdf</span>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(event) => setPodCoverPdf(event.target.files?.[0] ?? null)}
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-slate-600">interiorPdfUrl</span>
                <input
                  value={podInteriorPdfUrl}
                  onChange={(event) => setPodInteriorPdfUrl(event.target.value)}
                  placeholder="https://..."
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-slate-600">coverPdfUrl</span>
                <input
                  value={podCoverPdfUrl}
                  onChange={(event) => setPodCoverPdfUrl(event.target.value)}
                  placeholder="https://..."
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
            </div>
          </>
        ) : (
          <div className="grid gap-4">
            {contentKind === 'book' ? (
              <label className="grid gap-1">
                <span className="text-slate-600">Book File *</span>
                <input
                  type="file"
                  accept=".pdf,.epub,.mobi,.txt,application/pdf,application/epub+zip"
                  onChange={(event) => setBookFile(event.target.files?.[0] ?? null)}
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </label>
            ) : null}

            {contentKind === 'audio' ? (
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px]">
                <label className="grid gap-1">
                  <span className="text-slate-600">Audio File *</span>
                  <input
                    type="file"
                    accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                    onChange={(event) => setAudioFile(event.target.files?.[0] ?? null)}
                    className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                  />
                  <span className="text-xs text-slate-500">
                    {audioFile ? `Selected: ${audioFile.name}` : 'Upload an existing file or record a new one in Audio Studio.'}
                  </span>
                </label>

                <div className="rounded-2xl border border-orange-200 bg-orange-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Start Recording</p>
                  <p className="mt-2 text-sm text-slate-600">
                    Open a dedicated recorder page with microphone capture, noise cancellation, and simple audio polish controls.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/create-content/audio-recorder')}
                    className="mt-4 w-full rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    Open Audio Studio
                  </button>
                </div>
              </div>
            ) : null}

            {contentKind === 'animation' ? (
              <div className="grid gap-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <label className="grid gap-1">
                    <span className="text-slate-600">Trailer *</span>
                    <input
                      type="file"
                      accept="video/*,.mp4,.webm,.ogg,.mov,.avi,.mkv"
                      onChange={(event) => setTrailerFile(event.target.files?.[0] ?? null)}
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                    <span className="text-xs text-slate-500">{trailerFile ? `Selected: ${trailerFile.name}` : 'Upload a trailer clip or use Video Studio.'}</span>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-slate-600">Full Video *</span>
                    <input
                      type="file"
                      accept="video/*,.mp4,.webm,.ogg,.mov,.avi,.mkv"
                      onChange={(event) => setFullVideoFile(event.target.files?.[0] ?? null)}
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                    <span className="text-xs text-slate-500">{fullVideoFile ? `Selected: ${fullVideoFile.name}` : 'Upload the main video or record one in Video Studio.'}</span>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-slate-600">Picture *</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => setPictureFile(event.target.files?.[0] ?? null)}
                      className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                    />
                    <span className="text-xs text-slate-500">{pictureFile ? `Selected: ${pictureFile.name}` : 'Upload a still image or let Video Studio generate one.'}</span>
                  </label>
                </div>

                <div className="rounded-2xl border border-orange-200 bg-orange-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Start Video Recording</p>
                  <p className="mt-2 text-sm text-slate-600">
                    Open a dedicated video studio with camera recording, simple scene filters, brightness and contrast controls, and an auto-generated picture for your draft.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/create-content/video-recorder')}
                    className="mt-4 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    Open Video Studio
                  </button>
                </div>
              </div>
            ) : null}

            {(contentKind === 'book' || contentKind === 'audio') ? (
              <label className="grid gap-1">
                <span className="text-slate-600">Content Cover Image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setCoverFile(event.target.files?.[0] ?? null)}
                  className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
                <span className="text-xs text-slate-500">{coverFile ? `Selected: ${coverFile.name}` : 'Optional cover image for book or audio content.'}</span>
              </label>
            ) : null}
          </div>
        )}
      </section>

      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={feedback.status === 'pending'}
          className="flex-1 rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
        >
          {feedback.status === 'pending'
            ? contentKind === 'pod'
              ? 'Saving Draft...'
              : 'Publishing...'
            : contentKind === 'pod'
              ? 'Save POD Draft'
              : 'Publish Content'}
        </button>
        <button
          type="button"
          onClick={() => {
            setTitle('');
            setSummary('');
            setCategoryKey('');
            setLanguage('en');
            resetTypeSpecificFields('book');
            setFeedback(initialFeedback);
          }}
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
        >
          Reset
        </button>
      </div>

      {feedback.status !== 'idle' ? (
        <p className={`text-xs font-medium ${getFeedbackTone(feedback.status)}`}>{feedback.message}</p>
      ) : null}
      </form>

      {publisherCongrats.isOpen ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/55 p-4">
          <article className="w-full max-w-md rounded-3xl border border-orange-100 bg-white p-6 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.55)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-orange-600">Publisher Unlocked</p>
            <h3 className="mt-2 text-xl font-semibold text-slate-900">Congratulations</h3>
            <p className="mt-3 text-sm text-slate-600">
              {publisherCongrats.publisherName} has published content and is now listed on the Publishers page.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setPublisherCongrats({ isOpen: false, publisherName: '' });
                  navigate('/publishers');
                }}
                className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
              >
                Open Publishers Page
              </button>
              <button
                type="button"
                onClick={() => setPublisherCongrats({ isOpen: false, publisherName: '' })}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </article>
        </div>
      ) : null}
    </>
  );
};

export default BuyerCreateContentPanel;
